import type { ReactNode } from 'react'

/**
 * A table on a desktop and a stack of cards on a phone, from one description of the columns.
 *
 * **Why not just scroll it sideways.** That is what these tables did, and the layout check calls it
 * what it is: 544px of columns inside 343px hides 201px of them behind a gesture with no scrollbar,
 * no fade and no hint they exist. On the leaderboard the two columns that survive are the rank and
 * the name — every number the page exists to publish is off-screen. `BetLedger` reached the same
 * conclusion by hand and stacks into cards below `sm`; this is that answer written once, because
 * three fantasy tables needed it and a fourth will.
 *
 * A column says where it goes on a phone, since a card is not a narrower table — it has a subject
 * and some facts about it, not five equal cells. `index` and `title` make the card's first line,
 * `lead` is the one number worth reading at a glance, and `detail` becomes a labelled pair below.
 */
export interface Column<T> {
  /** Column heading, and the label on a phone. */
  head: string
  cell: (row: T) => ReactNode
  /** Numbers right-align; text does not. */
  align?: 'right'
  /** Where this lands on a phone. Defaults to `detail`. */
  card?: 'index' | 'title' | 'lead' | 'detail'
  /** Applied to the `td` only, e.g. a tone that depends on the row. */
  className?: (row: T) => string
}

export function DataTable<T>({
  columns,
  rows,
  keyOf,
}: {
  columns: Column<T>[]
  rows: T[]
  keyOf: (row: T) => string | number
}) {
  const index = columns.find((column) => column.card === 'index')
  const title = columns.find((column) => column.card === 'title')
  const lead = columns.find((column) => column.card === 'lead')
  const details = columns.filter((column) => (column.card ?? 'detail') === 'detail')

  return (
    <>
      <div className="hidden min-w-0 overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-ink-faint">
              {columns.map((column, at) => (
                <th
                  key={column.head}
                  className={`py-2 font-normal ${at === columns.length - 1 ? '' : 'pr-2'} ${
                    column.align === 'right' ? 'text-right' : ''
                  }`}
                >
                  {column.head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={keyOf(row)} className="border-b border-line">
                {columns.map((column, at) => (
                  <td
                    key={column.head}
                    className={`min-w-0 py-2 ${at === columns.length - 1 ? '' : 'pr-2'} ${
                      column.align === 'right' ? 'tabular text-right' : ''
                    } ${column.className?.(row) ?? ''}`}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="min-w-0 space-y-2 sm:hidden">
        {rows.map((row) => (
          <li key={keyOf(row)} className="min-w-0 rounded border border-line bg-surface px-3 py-2">
            <div className="flex min-w-0 items-baseline justify-between gap-2 text-sm">
              <span className="flex min-w-0 items-baseline gap-2">
                {index !== undefined ? (
                  <span className="tabular shrink-0 text-ink-faint">{index.cell(row)}</span>
                ) : null}
                {title !== undefined ? <span className="min-w-0">{title.cell(row)}</span> : null}
              </span>
              {lead !== undefined ? (
                <span className={`tabular shrink-0 ${lead.className?.(row) ?? ''}`}>
                  {lead.cell(row)}
                </span>
              ) : null}
            </div>

            <dl className="mt-1 flex min-w-0 flex-wrap gap-x-4 text-xs">
              {details.map((column) => (
                <div key={column.head} className="flex min-w-0 items-baseline gap-1">
                  <dt className="text-ink-faint">{column.head}</dt>
                  <dd className={`tabular text-ink-soft ${column.className?.(row) ?? ''}`}>
                    {column.cell(row)}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </>
  )
}
